export interface SseFrame<T = unknown> {
  event: string
  data: T
}

function parseFrame(raw: string): SseFrame | null {
  let event = 'message'
  const data: string[] = []
  for (const line of raw.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
    if (field === 'event') event = value
    if (field === 'data') data.push(value)
  }
  if (data.length === 0) return null
  const rawData = data.join('\n')
  try {
    return { event, data: JSON.parse(rawData) as unknown }
  } catch {
    return { event, data: rawData }
  }
}

/** Parse UTF-8 text/event-stream frames, including frames split across network chunks. */
export async function* parseSseStream(stream: ReadableStream<Uint8Array>): AsyncGenerator<SseFrame> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  // A CRLF can split across two network chunks; hold a trailing CR until the next chunk says whether LF follows.
  // Bare CR is a line ending too (the SSE spec allows CRLF, LF and CR).
  let heldCr = false
  try {
    while (true) {
      const { done, value } = await reader.read()
      let chunk = decoder.decode(value, { stream: !done })
      if (heldCr) {
        chunk = `\r${chunk}`
        heldCr = false
      }
      if (!done && chunk.endsWith('\r')) {
        heldCr = true
        chunk = chunk.slice(0, -1)
      }
      buffer += chunk.replace(/\r\n?/g, '\n')
      let boundary = buffer.indexOf('\n\n')
      while (boundary !== -1) {
        const frame = parseFrame(buffer.slice(0, boundary))
        buffer = buffer.slice(boundary + 2)
        if (frame) yield frame
        boundary = buffer.indexOf('\n\n')
      }
      if (done) break
    }
    const finalFrame = parseFrame(buffer)
    if (finalFrame) yield finalFrame
  } finally {
    reader.releaseLock()
  }
}
